import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, Mail, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, toTakkState } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useLang } from "@/lib/i18n";

export default function Bekreft() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const { verificationId, epost } = state || {};
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const complete = code.length === 6;

  useEffect(() => {
    if (complete && !busy) confirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  if (!verificationId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#1A1715] px-6 text-center">
        <Logo />
        <p className="text-[#A8A29E]">{t("Ingen pågående registrering. Start på nytt.", "No registration in progress. Please start again.")}</p>
        <Link to="/gave/registrer" className="rounded-full bg-[#D4AF37] px-8 py-3 font-semibold text-[#1A1715]" data-testid="go-to-registration-btn">
          {t("Til registrering", "Go to sign-up")}
        </Link>
      </div>
    );
  }

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.post("/verify/confirm", { verification_id: verificationId, code });
      navigate("/takk", { state: toTakkState(res.data), replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCode("");
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/verify/resend", { verification_id: verificationId });
      toast.success(t("Ny kode er sendt", "A new code has been sent"));
      setCooldown(60);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const slot = "h-14 w-11 border-white/15 bg-[#1A1715] text-2xl text-[#FDFBF7] first:rounded-l-xl last:rounded-r-xl sm:w-12";

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#1A1715]">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col px-5 py-8 sm:py-12">
        <header className="flex items-center justify-center animate-fade-in">
          <Logo />
        </header>

        <main className="mt-10 flex-1 text-center">
          <div className="animate-fade-up">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl font-600 text-[#FDFBF7] sm:text-5xl">{t("Bekreft at det er deg", "Confirm it's you")}</h1>
            <p className="mx-auto mt-4 max-w-md text-base text-[#FDFBF7]/90">
              {t("Vi har sendt en 6-sifret kode til e-posten din. Skriv den inn under for å fullføre registreringen.", "We've sent a 6-digit code to your email. Enter it below to complete your sign-up.")}
            </p>
          </div>

          <div className="mx-auto mt-8 flex max-w-sm flex-col items-center gap-6 animate-fade-up rounded-3xl border border-white/10 bg-[#282421]/90 p-6 shadow-2xl sm:p-8" style={{ animationDelay: "0.15s" }}>
            <div className="flex w-full flex-col items-center gap-2" data-testid="sent-to">
              <span className="inline-flex items-center gap-2 text-sm text-[#A8A29E]"><Mail className="h-4 w-4 text-[#D4AF37]" /> {t("Kode fra e-post", "Code from email")} · {epost}</span>
              <InputOTP maxLength={6} value={code} onChange={setCode} disabled={busy} data-testid="otp-input" autoFocus>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className={slot} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <button
              type="button"
              onClick={confirm}
              disabled={!complete || busy}
              data-testid="confirm-code-btn"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-base font-700 uppercase tracking-wide text-[#1A1715] transition-colors hover:bg-[#E5C564] disabled:cursor-not-allowed disabled:bg-[#D4AF37]/25 disabled:text-[#1A1715]/50"
            >
              {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("Bekrefter…", "Confirming…")}</> : t("Bekreft kode", "Confirm code")}
            </button>

            <button
              type="button"
              onClick={resend}
              disabled={cooldown > 0 || busy}
              data-testid="resend-code-btn"
              className="inline-flex items-center gap-2 text-sm text-[#A8A29E] transition-colors hover:text-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RotateCw className="h-4 w-4" />
              {cooldown > 0 ? `${t("Send koden på nytt", "Resend code")} (${cooldown}s)` : t("Send koden på nytt", "Resend code")}
            </button>
          </div>

          <p className="mt-6 text-xs text-[#A8A29E]">{t("Koden er gyldig i 10 minutter. Sjekk også søppelpost-mappen.", "The code is valid for 10 minutes. Check your spam folder too.")}</p>
        </main>

        <footer className="mt-8 text-center text-xs text-[#A8A29E]/60">
          <Link to="/gave/registrer" className="hover:text-[#D4AF37]" data-testid="back-to-registration">← {t("Tilbake til registrering", "Back to sign-up")}</Link>
        </footer>
      </div>
    </div>
  );
}
